/**
 * SessionStatusProgress Component
 * 
 * A combined session status and timer component that displays:
 * - Connection status text on top
 * - Live progress bar showing session time remaining
 * - Dynamic colors based on session status and remaining time
 */

import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { dsColors, resolveToken } from "../../../utils/dsTokens";
import {
  LinkOff,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { Icon } from "../../../components/ds";
import { sessionStatusOf, isAccountVerified, isPaperAccount } from "../accountStatus";

/**
 * Takes the ACCOUNT and derives the session itself, rather than accepting a
 * `sessionStatus` string. It used to take the raw `account.session_status`, which
 * means "a session row exists" — not "the session is live" — and gated its timer on
 * `sessionStatus !== "active"`. That landed on the right answer only by coincidence,
 * and AuthContext can push a 'expired' value over the WS that this component never
 * handled (it fell through to "Disconnected"). See ../accountStatus.
 */
const SessionStatusProgress = ({
  account,
  size = "small", // "small", "medium", "large"
  className = "",
}) => {
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null);
  const [progress, setProgress] = useState(0);

  const session = sessionStatusOf(account);
  const verified = isAccountVerified(account);
  const paper = isPaperAccount(account);
  const sessionExpiresAt = session.expiresAt;

  // Calculate and update session time remaining
  useEffect(() => {
    if (!sessionExpiresAt) {
      setSessionTimeLeft(null);
      setProgress(0);
      return;
    }

    const calculateTimeLeft = () => {
      const expiryTime = new Date(sessionExpiresAt).getTime();
      const now = new Date().getTime();
      const timeLeft = expiryTime - now;

      if (timeLeft <= 0) {
        setSessionTimeLeft(null);
        setProgress(0);
        return;
      }

      // Calculate total session duration (assuming 24 hours)
      const totalDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      const progressValue = Math.max(
        0,
        Math.min(100, (timeLeft / totalDuration) * 100)
      );

      const hours = Math.floor(timeLeft / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

      setSessionTimeLeft({ hours, minutes, seconds });
      setProgress(progressValue);
    };

    const timer = setInterval(calculateTimeLeft, 1000);
    calculateTimeLeft(); // Initial calculation

    return () => clearInterval(timer);
  }, [sessionExpiresAt]);

  // Get connection status properties
  const getConnectionStatus = () => {
    const isConnected = session.isActive;

    // Paper first: there is no broker to verify against and no session to expire, so
    // every branch below is meaningless for it. It used to fall into "Account Not
    // Verified" — a red error on an account that is working exactly as intended.
    if (paper) {
      return {
        text: "Always connected",
        icon: <Icon name="success" size={18} />,
        color: dsColors.success,
      };
    }

    if (!verified) {
      return {
        text: "Account Not Verified",
        icon: <Icon name="error" size={18} />,
        color: dsColors.danger, // --danger
      };
    }

    // NOTE: every branch used to also return bgColor/darkBgColor — nine hardcoded
    // hexes (#fef2f2, #450a0a, #ecfdf5, …) that NOTHING read. The Box below paints
    // its own translucent surface. They were removed rather than tokenised: dead
    // theming is worse than none, because it reads as though the theme is handled.
    if (isConnected && sessionTimeLeft) {
      const { hours } = sessionTimeLeft;
      if (hours < 1) {
        return {
          text: "Session Expiring Soon",
          icon: <Icon name="pending" size={18} />,
          color: dsColors.danger, // --danger
        };
      } else if (hours < 2) {
        return {
          text: "Session Active (Low Time)",
          icon: <Icon name="pending" size={18} />,
          color: dsColors.warning, // --warning
        };
      } else {
        return {
          text: "Session Active",
          icon: <Icon name="success" size={18} />,
          color: dsColors.success, // --success
        };
      }
    } else if (session.hasSession) {
      // Had a session; the clock says it is gone. Previously gated on the raw
      // session_status, which reports "active" for a lapsed session too.
      return {
        text: "Session Expired",
        icon: <Icon name="error" size={18} />,
        color: dsColors.danger, // --danger
      };
    } else {
      return {
        text: "Disconnected",
        icon: <LinkOff fontSize="small" />,
        // Was #6b7280 (gray-500): the only status colour here not from a token.
        // MUST be resolveToken, NOT the literal string "var(--fg-3)" — status.color
        // is fed to MUI's alpha() below, which throws on a var(). That throw took the
        // whole app to a blank page while the build stayed green. This is exactly what
        // utils/dsTokens exists for; every sibling here uses dsColors for the same
        // reason. Resolved per render, so it still follows a theme switch.
        color: resolveToken("--fg-3", "#6b7280"),
      };
    }
  };

  // Format time display
  const formatTimeLeft = (timeLeft) => {
    if (!timeLeft) return "";
    if (timeLeft.hours < 1) {
      return `${timeLeft.minutes}m ${timeLeft.seconds}s`;
    }
    return `${timeLeft.hours}h ${timeLeft.minutes}m`;
  };

  const status = getConnectionStatus();
  // Derived, not the raw field. Paper has no countdown to render.
  const hasTimer = session.isActive && !!sessionTimeLeft;

  // Size variants
  const sizes = {
    small: {
      height: 32,
      text: "text-xs",
      padding: "px-3 py-2",
    },
    medium: {
      height: 40,
      text: "text-sm", 
      padding: "px-4 py-2.5",
    },
    large: {
      height: 48,
      text: "text-base",
      padding: "px-5 py-3",
    },
  };

  const sizeConfig = sizes[size] || sizes.small;

  return (
    <Box
      className={`relative ${sizeConfig.padding} rounded-xl transition-all duration-300 ${className}`}
      sx={{
        background: (theme) => 
          theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.02)' 
            : 'rgba(0, 0, 0, 0.02)',
        backdropFilter: 'blur(10px)',
        border: (theme) => 
          theme.palette.mode === 'dark'
            ? `1px solid rgba(255, 255, 255, 0.1)`
            : `1px solid rgba(0, 0, 0, 0.1)`,
        overflow: "hidden",
        minWidth: "140px",
        position: 'relative',
        '&::before': hasTimer ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, ${alpha(status.color, 0.05)} 0%, ${alpha(status.color, 0.05)} ${progress}%, transparent ${progress}%)`,
          transition: 'all 1s ease-in-out',
          zIndex: 0,
        } : {},
      }}
    >
      {/* Content container */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: sizeConfig.height,
        }}
      >
        {/* Status text and icon */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box 
            sx={{ 
              color: status.color, 
              display: "flex", 
              alignItems: "center",
              opacity: 0.9,
            }}
          >
            {status.icon}
          </Box>
          <Typography
            className={sizeConfig.text}
            sx={{
              color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
              fontWeight: 500,
              lineHeight: 1.2,
              letterSpacing: '0.01em',
            }}
          >
            {status.text}
          </Typography>
        </Box>

        {/* Timer display */}
        {hasTimer && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              background: alpha(status.color, 0.1),
            }}
          >
            <Typography
              className={sizeConfig.text}
              sx={{
                color: status.color,
                fontWeight: 600,
                fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace",
                lineHeight: 1,
                fontSize: sizeConfig.text === 'text-xs' ? '0.75rem' : '0.875rem',
              }}
            >
              {formatTimeLeft(sessionTimeLeft)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Subtle progress indicator - modern thin line */}
      {hasTimer && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            height: 2,
            width: `${progress}%`,
            background: `linear-gradient(90deg, ${status.color}, ${alpha(status.color, 0.7)})`,
            borderRadius: '0 0 12px 0',
            transition: "all 1s ease-in-out",
            boxShadow: `0 0 8px ${alpha(status.color, 0.3)}`,
          }}
        />
      )}

      {/* Status indicator dot */}
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: status.color,
          boxShadow: `0 0 6px ${alpha(status.color, 0.4)}`,
          animation: hasTimer && sessionTimeLeft?.hours < 1 ? 'pulse 2s infinite' : 'none',
          '@keyframes pulse': {
            '0%, 100%': {
              opacity: 1,
              transform: 'scale(1)',
            },
            '50%': {
              opacity: 0.7,
              transform: 'scale(1.2)',
            },
          },
        }}
      />
    </Box>
  );
};

export default SessionStatusProgress;
